import * as fs from "node:fs";
import { CHANNEL_LATEST_URL, channelUrlForVersion, platformTag } from "@m8t-stack/platform-release";
import { validateManifest, type ReleaseManifest } from "./release-manifest.js";
import { getGhToken } from "./gh-helpers.js";
import { LocalCliError } from "./errors.js";

export { CHANNEL_LATEST_URL, channelUrlForVersion };

export type ManifestSource =
  | { file: string }
  | { url: string }
  | { channel: true; version?: string };

/**
 * A channel URL is a POINTER. Asking a channel for a specific version means
 * swapping the pointer segment for that version's tag — one rule, and the ONE
 * both the updater's forward fetch (converge.ts) and its rollback fetch
 * (platform-health-gate.ts) use, so neither ever needs GitHub repo auth against
 * a custom test/mirror host.
 *
 * GitHub spells a pointer two ways and both are handled by the same rule:
 *   /releases/latest/download/<asset>      — the built-in `latest` pointer
 *   /releases/download/<pointer>/<asset>   — any release tag used as a pointer,
 *                                            e.g. the rolling `canary` tag
 *
 * This used to special-case only the first shape, with everything else fetched
 * verbatim. That was fine while `latest` was the only channel, and quietly
 * wrong the moment a second one existed: a canary install would resolve EVERY
 * per-version request — including the health gate's rollback target — straight
 * back to the canary pointer, i.e. to the very build it was rolling away from.
 *
 * The default channel still resolves through `{ channel: true }` rather than a
 * URL, so the canonical path is unchanged. A non-GitHub custom URL is still
 * fetched verbatim (the caller asserts the version).
 */
export function manifestSourceForVersionTag(channelUrl: string, version: string): ManifestSource {
  // Normalize to the canonical tag FIRST (via the one helper) so a bare version
  // (e.g. the rollback path's previousPlatformVersion) never produces a
  // `…/download/0.2.0/…` URL that 404s against the tag-keyed release asset.
  const tag = platformTag(version);
  const custom = channelUrl.trim();
  if (!custom || custom === CHANNEL_LATEST_URL) return { channel: true, version: tag };
  const swapped = custom.replace(POINTER_SEGMENT, `/releases/download/${tag}/`);
  return { url: swapped };
}

/** The pointer segment of a GitHub release URL, in either spelling. Replacing it
 *  is what turns "the channel" into "this exact version". */
const POINTER_SEGMENT = /\/releases\/(?:latest\/download|download\/[^/]+)\//;

interface Deps {
  fetchImpl?: typeof fetch;
  ghToken?: () => Promise<string>;
  readFile?: (p: string) => string;
}

export async function fetchManifest(source: ManifestSource, deps: Deps = {}): Promise<ReleaseManifest> {
  const readFile = deps.readFile ?? ((p: string) => fs.readFileSync(p, "utf8"));
  const fetchImpl = deps.fetchImpl ?? fetch;
  const ghToken = deps.ghToken ?? getGhToken;

  let raw: string;
  if ("file" in source) {
    try {
      raw = readFile(source.file);
    } catch (e) {
      throw new LocalCliError({ code: "PLATFORM_MANIFEST_READ_FAILED", message: `Could not read manifest file '${source.file}': ${(e as Error).message}` });
    }
  } else {
    const url = "url" in source ? source.url : source.version ? channelUrlForVersion(source.version) : CHANNEL_LATEST_URL;
    // gh token is best-effort — the day the repo goes public it becomes optional.
    let token: string | undefined;
    try { token = await ghToken(); } catch { token = undefined; }
    const res = await fetchImpl(url, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
    if (!res.ok) {
      throw new LocalCliError({
        code: "PLATFORM_MANIFEST_FETCH_FAILED",
        message: `GET ${url} returned HTTP ${res.status.toString()}.`,
        hint: "The release channel is auth-gated until the repo is public. Run 'gh auth login', or pass --manifest-file / --manifest-url.",
      });
    }
    raw = JSON.stringify(await res.json());
  }

  let parsed: unknown;
  try { parsed = JSON.parse(raw); } catch (e) {
    throw new LocalCliError({ code: "PLATFORM_MANIFEST_PARSE_FAILED", message: `Manifest is not valid JSON: ${(e as Error).message}` });
  }
  const errors = validateManifest(parsed);
  if (errors.length > 0) {
    throw new LocalCliError({
      code: "PLATFORM_MANIFEST_INVALID",
      message: `Fetched manifest failed validation:\n  - ${errors.join("\n  - ")}`,
    });
  }
  return parsed as ReleaseManifest;
}
